import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid2 as Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

const STATUSES = [
  "Unassigned",
  "Assigned",
  "Started",
  "Completed",
  "Cancelled",
];

const STATUS_COLORS = {
  Completed: "success",
  Started: "info",
  Assigned: "secondary",
  Unassigned: "warning",
  Cancelled: "error",
};

const formatDate = (value) =>
  value ? new Date(value).toLocaleString("en-US") : "—";

const StatusChip = ({ status, size = "small" }) => (
  <Chip
    label={status || "Unknown"}
    color={STATUS_COLORS[status] || "default"}
    size={size}
    variant={status === "Unassigned" ? "outlined" : "filled"}
  />
);

const StatusChips = ({ statuses, fallbackStatus }) => {
  const values = statuses?.length ? statuses : [fallbackStatus];

  return (
    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
      {values.filter(Boolean).map((status) => (
        <StatusChip key={status} status={status} />
      ))}
    </Box>
  );
};

const NotaryRequestsGrid = ({ currentSession, refreshKey = 0 }) => {
  const tabletSize = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [notaryOptions, setNotaryOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statuses, setStatuses] = useState([]);
  const [notaryId, setNotaryId] = useState("");
  const [sessionType, setSessionType] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [sortModel, setSortModel] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    setPaginationModel((previous) => ({ ...previous, page: 0 }));
  }, [debouncedSearch, statuses, notaryId, sessionType, fromDate, toDate]);

  const fetchRows = useCallback(async () => {
    if (!currentSession) return;
    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/admin/notarization-request-log`,
        {
          currentSession,
          currentPath: window.location.pathname,
          page: paginationModel.page,
          rowsPerPage: paginationModel.pageSize,
          search: debouncedSearch,
          statuses,
          notaryId,
          sessionType,
          fromDate: fromDate ? fromDate.format("YYYY-MM-DD") : "",
          toDate: toDate ? toDate.format("YYYY-MM-DD") : "",
          sortModel,
        },
        { withCredentials: true },
      );
      setRows(response.data?.data?.rows || []);
      setRowCount(response.data?.data?.totalCount || 0);
      setNotaryOptions(response.data?.data?.notaries || []);
      setError("");
    } catch (requestError) {
      setError("Unable to load the notarization request log.");
    } finally {
      setLoading(false);
    }
  }, [
    currentSession,
    paginationModel,
    debouncedSearch,
    statuses,
    notaryId,
    sessionType,
    fromDate,
    toDate,
    sortModel,
  ]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows, refreshKey]);

  const clearFilters = () => {
    setSearch("");
    setStatuses([]);
    setNotaryId("");
    setSessionType("");
    setFromDate(null);
    setToDate(null);
    setSortModel([]);
  };

  const desktopColumns = useMemo(
    () => [
      {
        field: "projectAddress",
        headerName: "Project Address",
        minWidth: 260,
        flex: 1.6,
      },
      { field: "county", headerName: "County", minWidth: 110, flex: 0.6 },
      {
        field: "signers",
        headerName: "Signer(s)",
        minWidth: 220,
        flex: 1.3,
      },
      {
        field: "packageEmail",
        headerName: "Email",
        minWidth: 210,
        flex: 1.1,
      },
      { field: "sessionType", headerName: "Type", minWidth: 90, flex: 0.5 },
      {
        field: "status",
        headerName: "Status",
        minWidth: 190,
        flex: 1,
        renderCell: ({ row }) => (
          <StatusChips statuses={row.statuses} fallbackStatus={row.status} />
        ),
      },
      { field: "notary", headerName: "Notary", minWidth: 190, flex: 1 },
      {
        field: "lastActivity",
        headerName: "Last Activity",
        minWidth: 180,
        flex: 1,
        valueFormatter: (value) => formatDate(value),
      },
    ],
    [],
  );

  const mobileColumns = useMemo(
    () => [
      {
        field: "notarizationId",
        headerName: "Notarization Requests",
        flex: 1,
        sortable: false,
        renderCell: ({ row }) => (
          <Box sx={{ py: 1, width: "100%" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 1,
                mb: 0.5,
              }}
            >
              <Typography fontWeight={600}>
                {row.projectAddress || "—"}
              </Typography>
              <StatusChips
                statuses={row.statuses}
                fallbackStatus={row.status}
              />
            </Box>
            <Typography variant="body2">
              {row.sessionType} • {row.county || "No county"}
            </Typography>
            <Typography variant="body2">
              {row.signers || "No signer"}
            </Typography>
            <Typography variant="body2">
              {row.packageEmail || "No package email"}
            </Typography>
            <Typography variant="body2">
              {row.notary || "No notary assigned"}
            </Typography>
            <Typography variant="caption">
              {formatDate(row.lastActivity)}
            </Typography>
          </Box>
        ),
      },
    ],
    [],
  );

  return (
    <Card sx={{ backgroundColor: "#FCF2F0" }}>
      <CardContent sx={{ p: { xs: 1, md: 2 } }}>
        <Grid container spacing={1.5} sx={{ mb: 2 }} alignItems="center">
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              label="Search requests"
              placeholder="Address, signer, county, contractor, or notary"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                multiple
                value={statuses}
                onChange={(event) => setStatuses(event.target.value)}
                input={<OutlinedInput label="Status" />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                    {selected.map((status) => (
                      <StatusChip key={status} status={status} />
                    ))}
                  </Box>
                )}
              >
                {STATUSES.map((status) => (
                  <MenuItem key={status} value={status}>
                    <StatusChip status={status} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Notary</InputLabel>
              <Select
                value={notaryId}
                label="Notary"
                onChange={(event) => setNotaryId(event.target.value)}
              >
                <MenuItem value="">All notaries</MenuItem>
                {notaryOptions.map((notary) => (
                  <MenuItem key={notary.id} value={notary.id}>
                    {notary.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={sessionType}
                label="Type"
                onChange={(event) => setSessionType(event.target.value)}
              >
                <MenuItem value="">All types</MenuItem>
                <MenuItem value="RON">RON</MenuItem>
                <MenuItem value="IPEN">IPEN</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 1.5 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="From"
                value={fromDate}
                onChange={setFromDate}
                maxDate={toDate || dayjs()}
                slotProps={{ textField: { fullWidth: true, size: "small" } }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 1.5 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="To"
                value={toDate}
                onChange={setToDate}
                minDate={fromDate || undefined}
                maxDate={dayjs()}
                slotProps={{ textField: { fullWidth: true, size: "small" } }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid size={{ xs: 12, md: 1 }}>
            <Button fullWidth variant="outlined" onClick={clearFilters}>
              Clear
            </Button>
          </Grid>
        </Grid>

        {error && <Alert severity="error">{error}</Alert>}
        <DataGrid
          autoHeight
          rows={rows}
          columns={tabletSize ? mobileColumns : desktopColumns}
          loading={loading}
          rowCount={rowCount}
          paginationMode="server"
          sortingMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          disableRowSelectionOnClick
          getRowHeight={() => (tabletSize ? "auto" : 48)}
          sx={{
            "& .MuiDataGrid-columnHeader": {
              backgroundColor: "#333",
              color: "white",
            },
            "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus-within":
              { outline: "none" },
          }}
        />
      </CardContent>
    </Card>
  );
};

export default NotaryRequestsGrid;
